import hostConfig from "@/config/hostResolver";

/**
 * The default Whistle card, used for any branding field the card product leaves
 * null and for the whole card when Marqeta returns no branding (an unknown
 * product, or a Marqeta service that predates card product branding).
 */
export const DEFAULT_CARD_BRANDING = Object.freeze({
  issuerStatement:
    "This card is issued by Sutton Bank, Member FDIC, pursuant to a license from Visa U.S.A. Inc. The Whistle card is powered by Marqeta.",
  customerServiceUrl: "www.wewhistle.com/card-support",
  theme: "LIGHT",
  backgroundColor: "white",
  logoUrl: require("@/assets/Logo/whistle-logo-gradient-clean-v2.png"),
  logoPlacement: "LEFT",
  logoMaxWidth: 125,
  backgroundImageUrl: null,
});

const VISA_LOGOS = {
  DEFAULT: require("@/assets/Wallet/visa.png"),
  BLUE: require("@/assets/Wallet/visa_blue.png"),
  WHITE: require("@/assets/Wallet/visa_white.png"),
};

/**
 * Card asset paths from Marqeta are root-relative (`/card-assets/...`). Each
 * white label reaches Marqeta on its own host, so they resolve against the
 * Marqeta base this app calls. The leading slash drops any path prefix on that
 * base. Null when the path is unset, cannot be resolved, or resolves to a host
 * other than the Marqeta base's.
 */
export function cardAssetUrl(path, base = hostConfig.paymentsBaseUrl) {
  if (!path) return null;
  try {
    const url = new URL(path, base);
    return url.origin === new URL(base).origin ? url.href : null;
  } catch (e) {
    return null;
  }
}

export const DEFAULT_CARD_NAME = "Whistle Card";

const CARD_TYPES = ["virtual", "physical"];

/**
 * What to call the participant's card in wallet copy: the held card's name,
 * else the assigned product's name, else "Whistle Card". `marqeta` is the
 * GET /cards state in vuex. Without a type, a held virtual or physical card
 * wins over either assigned product. Null until GET /cards has loaded, rather
 * than showing "Whistle Card" until a cobrand name replaces it.
 */
export function cardDisplayName(marqeta, type) {
  if (!marqeta || !marqeta.loaded) return null;
  const types = type ? [type] : CARD_TYPES;
  for (const t of types) {
    const held = heldCardName(marqeta.cards, t);
    if (held) return held;
  }
  for (const t of types) {
    const assigned = assignedProductName(marqeta, t);
    if (assigned) return assigned;
  }
  return DEFAULT_CARD_NAME;
}

/** The name of the held card of this type, or null. */
export function heldCardName(cards, type) {
  const held = (cards || []).find((card) => card.type === type && card.displayName);
  return held ? held.displayName : null;
}

/** The name of the product new cards of this type are issued on, or null. */
export function assignedProductName(marqeta, type) {
  const product = marqeta && marqeta.assignedProducts && marqeta.assignedProducts[type];
  return (product && product.displayName) || null;
}

/**
 * The issuer statement of the product new cards of this type are issued on.
 * Null until GET /cards has loaded, so the legal sentence stays hidden rather
 * than flashing the default one; the default card's statement after that.
 */
export function assignedIssuerStatement(marqeta, type) {
  if (!marqeta || !marqeta.loaded) return null;
  const product = marqeta.assignedProducts && marqeta.assignedProducts[type];
  return (product && product.issuerStatement) || DEFAULT_CARD_BRANDING.issuerStatement;
}

/**
 * The cobrand card image stored on the client's internalCustom2, read while
 * feature-marqeta-card-products is off. Null to show the bundled default.
 */
export function clientCardImageUrl(userProfile) {
  const image = userProfile && userProfile.Client && userProfile.Client.internalCustom2;
  return image && image.startsWith("https") ? image : null;
}

/** The assigned physical product's card image, or null to show the bundled default. */
export function assignedPhysicalCardImageUrl(marqeta) {
  const physical = marqeta && marqeta.assignedProducts && marqeta.assignedProducts.physical;
  return cardAssetUrl(physical && physical.cardImagePath);
}

/**
 * True when the held card of this type is on a product other than the
 * assigned one, so replacing it issues a card with a new number and design.
 */
export function replacementChangesProduct(cards, type) {
  const held = (cards || []).find((card) => card.type === type);
  return !!held && held.isAssignedProduct === false;
}

/**
 * The virtual card's design from the `branding` returned by
 * GET /cards/PCI/virtual, with every unset field filled from the default card.
 */
export function resolveCardBranding(branding) {
  const source = branding || {};
  const pick = (field) =>
    source[field] === undefined || source[field] === null
      ? DEFAULT_CARD_BRANDING[field]
      : source[field];

  const theme = pick("theme");
  const dark = theme === "DARK";
  const visaLogoVariant = source.visaLogoVariant || (dark ? "WHITE" : "DEFAULT");

  return {
    issuerStatement: pick("issuerStatement"),
    customerServiceUrl: pick("customerServiceUrl"),
    theme,
    dark,
    backgroundColor: pick("backgroundColor"),
    backgroundImageUrl:
      cardAssetUrl(source.backgroundImagePath) || DEFAULT_CARD_BRANDING.backgroundImageUrl,
    logoUrl: cardAssetUrl(source.logoPath) || DEFAULT_CARD_BRANDING.logoUrl,
    logoPlacement: pick("logoPlacement"),
    logoMaxWidth: pick("logoMaxWidth"),
    visaLogoVariant: VISA_LOGOS[visaLogoVariant] ? visaLogoVariant : "DEFAULT",
    visaLogoUrl: VISA_LOGOS[visaLogoVariant] || VISA_LOGOS.DEFAULT,
  };
}
